import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Feather, Save, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PerfilDoAutor() {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    public_name: '',
    bio: '',
    main_genre: '',
    signature_phrase: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        public_name: user.public_name || '',
        bio: user.bio || '',
        main_genre: user.main_genre || '',
        signature_phrase: user.signature_phrase || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/profile`,
        formData,
        { withCredentials: true }
      );
      await checkAuth();
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="perfil-do-autor-page">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-light mb-2">Perfil do Autor</h1>
            <p className="text-muted-foreground">Sua identidade literária</p>
          </div>

          {/* Author Card */}
          <div className="bg-card border border-border/40 rounded-lg p-8 mb-8">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="relative">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <User className="w-12 h-12 text-amber-600" />
                  </div>
                )}
                <button 
                  className="absolute bottom-0 right-0 p-2 bg-card border border-border rounded-full hover:bg-muted transition-colors"
                  title="Alterar foto (em breve)"
                >
                  <Camera size={14} />
                </button>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h2 className="text-2xl font-serif mb-1">{user?.name || 'Autor'}</h2>
                <p className="text-muted-foreground mb-3">{user?.email}</p>
                {formData.signature_phrase && (
                  <p className="text-sm italic text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <Feather size={14} />
                    "{formData.signature_phrase}"
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-card border border-border/40 rounded-lg p-6">
              <h3 className="text-xl font-serif mb-6">Informações do Autor</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome Público / Pseudônimo</label>
                  <Input
                    type="text"
                    value={formData.public_name}
                    onChange={(e) => setFormData({ ...formData, public_name: e.target.value })}
                    placeholder="Como você quer ser conhecido como escritor"
                    data-testid="public-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Mini Biografia</label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Conte um pouco sobre sua jornada como escritor..."
                    rows={4}
                    data-testid="bio-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Gênero Literário Principal</label>
                  <Input
                    type="text"
                    value={formData.main_genre}
                    onChange={(e) => setFormData({ ...formData, main_genre: e.target.value })}
                    placeholder="Romance, Ficção Científica, Fantasia..."
                    data-testid="genre-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Frase de Assinatura</label>
                  <Input
                    type="text"
                    value={formData.signature_phrase}
                    onChange={(e) => setFormData({ ...formData, signature_phrase: e.target.value })}
                    placeholder="Sua frase inspiradora pessoal"
                    data-testid="signature-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Esta frase aparecerá no seu perfil público
                  </p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              data-testid="save-profile-button"
            >
              <Save className="mr-2" size={20} />
              {loading ? 'Salvando...' : 'Salvar Perfil'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
